import {
  HStack,
  ICON_SIZES,
  Icon,
  IconName,
  IndicatorBadge,
  Text,
  VStack,
  formattedUTCDate,
  isCalendarSpread,
  useMarketsContext,
  useSecurity,
  type Security,
} from '@talos/kyoko';
import { useState } from 'react';
import { Section } from './styles';
import { getInfoRow } from './utils';

export const CmeStrategySummary = ({ security }: { security: Security }) => {
  const [expandedLeg, setExpandedLeg] = useState<number>(-1);

  if (!isCalendarSpread(security)) {
    return null;
  }

  return (
    <div data-testid="cme-legs-summary-panel">
      {security?.Legs?.map((leg, index) => {
        return (
          <CmeLegSummary
            key={leg.Symbol}
            legIndex={index}
            symbol={leg.Symbol}
            isExpanded={expandedLeg === index}
            onToggle={() => setExpandedLeg(index === expandedLeg ? -1 : index)}
          />
        );
      })}
    </div>
  );
};

export interface CmeLegSummaryProps {
  legIndex: number;
  isExpanded: boolean;
  onToggle: () => void;
  symbol: string;
}

export const CmeLegSummary = ({ legIndex, isExpanded, onToggle, symbol }: CmeLegSummaryProps) => {
  const security = useSecurity(symbol);
  const { marketsByName } = useMarketsContext();
  const exchange = marketsByName.get(security?.Markets?.[0] || '');
  const expiration = security?.Expiration
    ? formattedUTCDate(new Date(security?.Expiration), '{dd}{Mon}{yy}').toUpperCase()
    : '';

  return (
    <Section isExpanded={isExpanded} mt="spacing4" nonExpandedRows={1}>
      <HStack gap="spacing8" justifyContent="flex-start" onClick={onToggle} mb={isExpanded ? 'spacing12' : undefined}>
        <HStack flex={1} style={{ cursor: 'pointer' }} justifyContent="flex-start">
          <IndicatorBadge children={legIndex + 1} />
          <Text color="colorTextSubtle" whiteSpace="nowrap" fontSize="fontSizeSmall">
            {security?.DisplaySymbol ?? symbol}
          </Text>
        </HStack>
        <Icon
          icon={IconName.ChevronDown}
          rotate={isExpanded ? 0 : 180}
          size={ICON_SIZES.MEDIUM}
          variant={'gray["070"]'}
        />
      </HStack>
      {!isExpanded ? null : (
        <VStack alignItems="flex-start">
          {getInfoRow('Asset', security?.BaseCurrency)}
          {getInfoRow('Instrument', security?.ProductType)}
          {getInfoRow('Exchange', exchange?.DisplayName)}
          {getInfoRow('Expiry Date', expiration)}
        </VStack>
      )}
    </Section>
  );
};
